import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, FileCheck2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => (Number(n) ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : "—");
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } };

const Row = ({ label, children, mono }) => (
  <div style={{ display: "flex", gap: 14, padding: "10px 0", borderBottom: "1px solid #14503B" }} data-testid={`vc-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div style={{ flex: "0 0 42%", fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", color: "#6FD3AE", fontWeight: 700 }}>{label}</div>
    <div style={{ flex: 1, fontSize: 13.5, color: "#E7F3EE", fontFamily: mono ? "'Courier New',monospace" : "inherit", wordBreak: mono ? "break-all" : "normal" }}>{children}</div>
  </div>
);

export default function VerifyCertificate() {
  const { appId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/tax-credit/verify/${appId}`);
        if (alive) setData(r.data);
      } catch { if (alive) setData({ found: false }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [appId]);

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif", padding: "0 16px" }} data-testid="verify-certificate-page">
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "40px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <img src="/seutrade_logo.png" alt="SEUTRADE Financial Services" data-testid="vc-logo"
            style={{ width: 64, height: 64, borderRadius: "50%", background: "#fff", padding: 3, flex: "0 0 auto", boxShadow: "0 2px 10px rgba(0,0,0,.35)" }} />
          <div>
            <div style={{ color: "#6FD3AE", fontWeight: 800, letterSpacing: ".08em", fontSize: 13, textTransform: "uppercase" }}>SEUTRADE FINANCIAL SERVICES</div>
            <div style={{ color: "#9ec9ba", fontSize: 12 }}>Certificate Verification</div>
          </div>
        </div>

        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#9ec9ba", padding: "40px 0" }} data-testid="vc-loading">
            <Loader2 size={20} className="animate-spin" /> Verifying certificate…
          </div>
        )}

        {!loading && data && !data.found && (
          <div style={{ background: "#2a1512", border: "1px solid #6b2d22", borderRadius: 14, padding: 26 }} data-testid="vc-notfound">
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#ff9b86", fontWeight: 800, fontSize: 16 }}>
              <ShieldAlert size={22} /> Certificate not found
            </div>
            <p style={{ color: "#e7b9ae", fontSize: 13.5, marginTop: 10, lineHeight: 1.6 }}>
              No Solar Tax Credit Transfer Certificate matches this reference. If you scanned a printed certificate, confirm the QR code is intact, or contact the issuer to verify authenticity.
            </p>
          </div>
        )}

        {!loading && data && data.found && (
          <div data-testid="vc-result">
            <div style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ background: data.registration_valid ? "#0e3a2a" : "#3a2e0e", padding: "16px 22px", display: "flex", alignItems: "center", gap: 12 }} data-testid="vc-status-banner">
                {data.registration_valid
                  ? <ShieldCheck size={26} style={{ color: "#6FD3AE" }} />
                  : <ShieldAlert size={26} style={{ color: "#E4C97A" }} />}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: data.registration_valid ? "#9EEBCB" : "#E4C97A" }}>
                    {data.registration_valid ? "Certificate on record · registration format valid" : "Certificate on record · registration needs confirmation"}
                  </div>
                  <div style={{ fontSize: 12, color: "#9ec9ba", marginTop: 2 }}>{data.authority}</div>
                </div>
              </div>

              <div style={{ padding: "8px 22px 20px" }}>
                <Row label="Issuer">{data.issuer}</Row>
                <Row label="Transferee">{data.transferee || "—"}</Row>
                <Row label="Transferee TIN">{data.tin_masked || "—"}</Row>
                <Row label="Credit Amount">{money(data.credit_amount)} <span style={{ color: "#9ec9ba", fontSize: 11 }}>· in solar tax credit</span></Row>
                <Row label="Credit Year">{data.credit_year || "—"}</Row>
                <Row label="Issue Date">{fmtDate(data.issue_date)}</Row>
                <Row label="Certificate No.">{data.certificate_no}</Row>
                <Row label="IRS Registration No.">{data.registration_no} {data.registration_valid ? "✓" : "⚠"}</Row>
                <Row label="Master Agreement">{data.agreement_no}</Row>
                <Row label="E-Sign Hash (SHA-256)" mono>{data.esign_hash}</Row>
                {data.irs_filed && data.irs_filed.status && (
                  <Row label="IRS Forms Filed"><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FileCheck2 size={14} style={{ color: "#6FD3AE" }} /> {data.irs_filed.status}{data.irs_filed.at ? ` · ${new Date(data.irs_filed.at).toLocaleDateString()}` : ""}</span></Row>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
              <a href={`${API}/tax-credit/verify/${appId}/certificate.pdf`} target="_blank" rel="noopener noreferrer"
                data-testid="vc-view-pdf"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#6FD3AE", color: "#06160F", textDecoration: "none", padding: "11px 20px", borderRadius: 10, fontSize: 13, fontWeight: 800, letterSpacing: ".03em" }}>
                <FileText size={16} /> View the certificate PDF
              </a>
            </div>

            <div style={{ background: "#0A1813", border: "1px solid #123528", borderRadius: 12, padding: "14px 18px", marginTop: 16, fontSize: 12, color: "#9ec9ba", lineHeight: 1.6 }} data-testid="vc-disclaimer">
              This certificate evidences a one-time transfer of an eligible §48 solar Investment Tax Credit under IRC §6418. It is <b>NON-NEGOTIABLE</b> — not a check, draft or money order, not drawn on any bank or the U.S. Treasury, and has no cash value. Under IRC §6418(e)(2) the transferee may not re-transfer the credit. The credit is claimed only on the transferee's federal income tax return (Form 3800) with the §6418 Transfer Election Statement. This page confirms the certificate's recorded identifiers and e-sign hash; it is not issued or guaranteed by the IRS.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
